/**
 * Disegno di un elemento dello stage.
 *
 * Convenzione delle piantine di gara, deliberatamente mista:
 * **bersagli di fronte, campo in pianta**. Una sagoma vista dall'alto è un
 * trattino e non si riconosce; di fronte si distingue al volo una carta da un
 * popper da un piatto — che è tutto quello che serve a chi allestisce.
 *
 * **Il viewBox è in millimetri reali.** Prima era un 100×167 fisso con
 * `preserveAspectRatio="none"`, cioè ogni forma veniva stirata nel riquadro
 * che le toccava; ora ogni sagoma è disegnata nelle sue quote (450 × 570 per
 * il Target IPSC) e scalata, quindi le proporzioni sono quelle vere e una
 * Mini accanto a una Target è piccola nella misura giusta.
 *
 * Le zone A/C si disegnano davvero, con le quote del regolamento
 * (A 150 × 325, C 300 × 450 su 450 × 570). Senza, lo zoom sul bersaglio
 * ingrandisce una silhouette vuota e non c'è niente su cui cerchiare.
 *
 * Spec: docs/shooting/13-piano-revisione-test-federico.md §2.1
 */
import {
	elementSpec,
	idpaOutlinePath,
	ipscOutlinePath,
	popperCalibrationZone,
	popperOutlinePath,
} from "./stageElements";

const OUTLINE = "#0b0f14";
const SELECTED = "#00d68f";
const ZONE_LINE = "#1a1a1a";

/** Un ottagono inscritto nel riquadro: è la forma delle zone A e C. */
const zonePath = (cx, cy, w, h) => {
	const x0 = cx - w / 2;
	const x1 = cx + w / 2;
	const y0 = cy - h / 2;
	const y1 = cy + h / 2;
	const cut = Math.min(w, h) * 0.22;
	return [
		`M${x0 + cut} ${y0}`,
		`L${x1 - cut} ${y0}`,
		`L${x1} ${y0 + cut}`,
		`L${x1} ${y1 - cut}`,
		`L${x1 - cut} ${y1}`,
		`L${x0 + cut} ${y1}`,
		`L${x0} ${y1 - cut}`,
		`L${x0} ${y0 + cut}`,
		"Z",
	].join(" ");
};

export function StageShape({ type, widthPx, heightPx, selected }) {
	const spec = elementSpec(type);
	const stroke = selected ? SELECTED : OUTLINE;

	// ── Carta: profilo in quote reali + zone di punteggio ──
	if (spec.shape === "paper_ipsc" || spec.shape === "paper_idpa") {
		const isIdpa = spec.shape === "paper_idpa";
		// viewBox in millimetri: il profilo non viene mai stirato.
		const vw = spec.widthM * 1000;
		const vh = spec.heightM * 1000;
		const outline = isIdpa ? idpaOutlinePath(vw, vh) : ipscOutlinePath(vw, vh);
		// Lo spessore va espresso nel viewBox, che è in mm: 6 mm di tratto si
		// legge uguale su una Target e su una Micro.
		const sw = selected ? 14 : 7;

		return (
			<svg
				width={widthPx}
				height={heightPx}
				viewBox={`0 0 ${vw} ${vh}`}
				style={{ overflow: "visible" }}
			>
				<path d={outline} fill={spec.color} stroke={stroke} strokeWidth={sw} />

				{/* Bordo a punteggio nullo (FITDS 4.2.2: 5 mm, 3 sui Mini).
				    È la fascia che non assegna punti, e in una piantina serve
				    solo a ricordare che il bordo non vale. */}
				{spec.borderMm && (
					<path
						d={outline}
						fill="none"
						stroke={OUTLINE}
						strokeOpacity="0.35"
						strokeWidth={spec.borderMm * 2}
					/>
				)}

				{/* Zone, quando il bersaglio ne ha: un no-shoot non si spara e
				    disegnargliele sarebbe fuorviante. */}
				{spec.zones && (
					<>
						<path
							d={zonePath(
								vw / 2,
								vh / 2,
								vw * spec.zones.c.w,
								vh * spec.zones.c.h,
							)}
							fill="none"
							stroke={ZONE_LINE}
							strokeOpacity="0.32"
							strokeWidth={6}
						/>
						<path
							d={zonePath(
								vw / 2,
								vh / 2,
								vw * spec.zones.a.w,
								vh * spec.zones.a.h,
							)}
							fill="none"
							stroke={ZONE_LINE}
							strokeOpacity="0.45"
							strokeWidth={7}
						/>
					</>
				)}

				{/* IDPA: zone concentriche, forma diversa e regolamento diverso. */}
				{isIdpa && !spec.noShoot && (
					<>
						<circle
							cx={vw / 2}
							cy={vh * 0.56}
							r={vw * 0.17}
							fill="none"
							stroke={ZONE_LINE}
							strokeOpacity="0.42"
							strokeWidth={7}
						/>
						<circle
							cx={vw / 2}
							cy={vh * 0.56}
							r={vw * 0.3}
							fill="none"
							stroke={ZONE_LINE}
							strokeOpacity="0.28"
							strokeWidth={6}
						/>
					</>
				)}

				{/* No-shoot: la X del regolamento (4.1.3). È il segno che lo
				    distingue, e su una piantina in bianco e nero è l'unico. */}
				{spec.noShoot && (
					<path
						d={`M${vw * 0.16} ${vh * 0.12} L${vw * 0.84} ${vh * 0.88} ` +
							`M${vw * 0.84} ${vh * 0.12} L${vw * 0.16} ${vh * 0.88}`}
						stroke={OUTLINE}
						strokeWidth={12}
						strokeOpacity="0.8"
						fill="none"
					/>
				)}

				{/* Bersaglio mobile: il tratteggio alla base lo distingue da un
				    fisso senza aggiungere un'icona che ruberebbe spazio. */}
				{spec.moving && (
					<path
						d={`M${vw * 0.3} ${vh * 0.95} L${vw * 0.7} ${vh * 0.95}`}
						stroke="#facc15"
						strokeWidth={22}
						strokeDasharray="34 26"
					/>
				)}
			</svg>
		);
	}

	// ── Popper: testa tonda, collo, corpo a trapezio su base piatta ──
	// Il viewBox è in millimetri reali come per la carta, e non si stira:
	// il profilo precedente era un path fisso schiacciato nel riquadro, e un
	// mini popper schiacciato «sembra un omino».
	if (spec.shape === "steel_popper") {
		const vw = spec.widthM * 1000;
		const vh = spec.heightM * 1000;
		const zone = popperCalibrationZone(vw);
		return (
			<svg
				width={widthPx}
				height={heightPx}
				viewBox={`0 0 ${vw} ${vh}`}
				preserveAspectRatio="xMidYMax meet"
				style={{ overflow: "visible" }}
			>
				<path
					d={popperOutlinePath(vw, vh)}
					fill={spec.color}
					stroke={stroke}
					strokeWidth={selected ? 16 : 8}
					strokeLinejoin="round"
				/>
				{/* Zona di calibrazione (Appendice C2): il cerchio della testa
				    che il giudice colpisce per verificare il popper, ed è dove
				    si mira. */}
				<circle
					cx={zone.cx}
					cy={zone.cy}
					r={zone.r}
					fill={OUTLINE}
					fillOpacity="0.12"
					stroke={OUTLINE}
					strokeOpacity="0.35"
					strokeWidth={5}
				/>
			</svg>
		);
	}

	if (spec.shape === "steel_plate") {
		const r = Math.min(widthPx, heightPx) / 2;
		return (
			<svg
				width={widthPx}
				height={heightPx}
				viewBox={`0 0 ${widthPx} ${heightPx}`}
				style={{ overflow: "visible" }}
			>
				<circle
					cx={widthPx / 2}
					cy={heightPx / 2}
					r={r}
					fill={spec.color}
					stroke={stroke}
					strokeWidth={selected ? 3 : 2}
				/>
				{spec.noShoot && (
					<path
						d={`M${widthPx * 0.25} ${heightPx * 0.25} L${widthPx * 0.75} ${heightPx * 0.75} ` +
							`M${widthPx * 0.75} ${heightPx * 0.25} L${widthPx * 0.25} ${heightPx * 0.75}`}
						stroke={OUTLINE}
						strokeWidth="2"
						strokeOpacity="0.75"
					/>
				)}
			</svg>
		);
	}

	if (spec.shape === "steel_plate_rect") {
		return (
			<svg
				width={widthPx}
				height={heightPx}
				viewBox={`0 0 ${widthPx} ${heightPx}`}
				style={{ overflow: "visible" }}
			>
				<rect
					width={widthPx}
					height={heightPx}
					fill={spec.color}
					stroke={stroke}
					strokeWidth={selected ? 3 : 2}
				/>
			</svg>
		);
	}

	// Plate rack: sei piatti su telaio. Si disegna per quello che è, o sulla
	// piantina sembra un muro.
	if (spec.shape === "steel_rack") {
		const plates = 6;
		const step = widthPx / plates;
		const r = Math.min(step, heightPx) * 0.38;
		return (
			<svg
				width={widthPx}
				height={heightPx}
				viewBox={`0 0 ${widthPx} ${heightPx}`}
				style={{ overflow: "visible" }}
			>
				<rect
					width={widthPx}
					height={heightPx}
					fill="none"
					stroke={stroke}
					strokeWidth={selected ? 3 : 1.5}
				/>
				{Array.from({ length: plates }, (_, i) => (
					<circle
						key={i}
						cx={step * (i + 0.5)}
						cy={heightPx / 2}
						r={r}
						fill={spec.color}
						stroke={OUTLINE}
						strokeWidth="1.5"
					/>
				))}
			</svg>
		);
	}

	// ── Campo, in pianta ──
	const w = widthPx;
	const h = Math.max(heightPx, 4);
	return (
		<svg
			width={w}
			height={h}
			viewBox={`0 0 ${w} ${h}`}
			style={{ overflow: "visible" }}
		>
			{spec.shape === "fault" && (
				<line
					x1={0}
					y1={h / 2}
					x2={w}
					y2={h / 2}
					stroke={spec.color}
					strokeWidth={Math.max(h, 4)}
					strokeDasharray="10 7"
					strokeLinecap="round"
				/>
			)}
			{spec.shape === "panel" && (
				<rect
					width={w}
					height={h}
					fill={spec.color}
					stroke={stroke}
					strokeWidth="1"
				/>
			)}
			{spec.shape === "barricade" && (
				<>
					<rect width={w} height={h} fill={spec.color} />
					{/* Le barricate si disegnano rigate: è come si distinguono da
					    un muro pieno nelle piantine federali. */}
					{Array.from({ length: Math.max(2, Math.floor(w / 10)) }, (_, i) => (
						<line
							key={i}
							x1={(i + 1) * (w / (Math.floor(w / 10) + 1))}
							y1={0}
							x2={(i + 1) * (w / (Math.floor(w / 10) + 1))}
							y2={h}
							stroke={OUTLINE}
							strokeWidth="1"
						/>
					))}
				</>
			)}
			{spec.shape === "port" && (
				<rect
					width={w}
					height={h}
					fill="none"
					stroke={spec.color}
					strokeWidth={Math.max(h / 2, 3)}
					strokeDasharray="4 5"
				/>
			)}
			{spec.shape === "box" && (
				<rect
					width={w}
					height={h}
					fill="none"
					stroke={spec.color}
					strokeWidth="2.5"
					strokeDasharray="7 4"
				/>
			)}
			{spec.shape === "table" && (
				<rect
					width={w}
					height={h}
					rx="2"
					fill={spec.color}
					opacity="0.65"
					stroke={stroke}
					strokeWidth="1"
				/>
			)}
			{spec.shape === "activator" && (
				<>
					<circle
						cx={w / 2}
						cy={h / 2}
						r={Math.min(w, h) / 2}
						fill="none"
						stroke={spec.color}
						strokeWidth="2.5"
					/>
					<path
						d={`M${w / 2 - 3} ${h / 2 - 5} L${w / 2 + 4} ${h / 2} L${w / 2 - 3} ${h / 2 + 5} Z`}
						fill={spec.color}
					/>
				</>
			)}
			{selected && (
				<rect
					width={w}
					height={h}
					fill="none"
					stroke={SELECTED}
					strokeWidth="2"
				/>
			)}
		</svg>
	);
}
